import { cloneElement } from 'react';
import { Button, Header, HStack, Link, Text, VStack } from '@direct-frontend/components';
import { getDigitalOrderStatus } from '@/entities/digitalOrder';
import { isOrderEditable } from '@/entities/order';
import { CopyButton } from '@/features/CopyButton';
import { type SchemaDigitalOrder } from '@/shared/api/openApi';
import { OrderStatuses } from '@/shared/api/order';
import { Footer } from '@/shared/components/Footer';
import { formatPrice } from '@/shared/lib/formatPrice';
import { useMessageToast } from '@/shared/lib/hooks';
import { getRequestContext } from '@/shared/lib/requestContext';
import {
    DigitalCheckoutCharacteristic,
    DigitalCheckoutCharacteristicGroup,
} from './components/DigitalCheckoutCharacteristic';
import { getDigitalCheckoutFeedbackIcon } from './lib';
import styles from './DigitalCheckoutFeedback.module.css';

export interface DigitalCheckoutFeedbackProps {
    order: SchemaDigitalOrder;
}

export const DigitalCheckoutFeedback = ({ order }: DigitalCheckoutFeedbackProps) => {
    const {
        company: { companyShopUrl },
    } = getRequestContext();

    const { showToast } = useMessageToast({ position: 'top-center' });

    const onCopySuccess = () => {
        showToast({ title: 'Номер заказа скопирован' });
    };

    if (isOrderEditable(order)) {
        return null;
    }

    const finalPrice = Number(order.total_final_price);

    return (
        <VStack className={styles.root} alignItems="center">
            <VStack gap="16" alignItems="stretch" className={styles.content}>
                {cloneElement(getDigitalCheckoutFeedbackIcon(order), { className: styles.illustation })}

                <Header level="h2" textAlign="center">
                    {order.status === OrderStatuses.CANCELLED ? 'Заказ отменён' : 'Заказ оформлен'}
                </Header>

                <VStack gap="8">
                    <DigitalCheckoutCharacteristicGroup>
                        <HStack
                            className={styles.characteristic}
                            alignItems="center"
                            justifyContent="space-between"
                            gap="8"
                        >
                            <Text size="m" role="supplementary" className={styles.characteristicTitle}>
                                Номер заказа
                            </Text>
                            <CopyButton text={order.order_number} onSuccess={onCopySuccess}>
                                <Text size="m" bold className={styles.characteristicContent}>
                                    {order.order_number}
                                </Text>
                            </CopyButton>
                        </HStack>
                    </DigitalCheckoutCharacteristicGroup>

                    <DigitalCheckoutCharacteristicGroup>
                        <DigitalCheckoutCharacteristic title="Сумма заказа" content={formatPrice(finalPrice)} />
                        <DigitalCheckoutCharacteristic title="Статус заказа" content={getDigitalOrderStatus(order)} />
                    </DigitalCheckoutCharacteristicGroup>
                </VStack>
            </VStack>

            <Button as={Link} href={companyShopUrl} color="accent" width="max" size="m">
                Вернуться в магазин
            </Button>

            <Footer centered />
        </VStack>
    );
};
